// v1 route registry seed (D-28): model ids and prices are data, editable in
// the UI after the first deploy - a wrong vendor model id or a stale price is
// a data fix here plus a `putRouteIfAbsent` re-seed, never a code change.
// Prices are estimates gathered 2026-09-04 for planning. `verifyBeforeEnable`
// marks a route whose exact vendor model id could not be confirmed while
// planning; it is seeded `enabled: false` until the owner confirms it.

import type { ModelRoute } from "./types.js";

const SEEDED_AT = "2026-09-04T00:00:00.000Z";

function route(partial: Omit<ModelRoute, "entityType" | "updatedAt" | "enabled"> & { verify?: true }): ModelRoute {
  const { verify, ...rest } = partial;
  return {
    entityType: "ModelRoute",
    updatedAt: SEEDED_AT,
    enabled: !verify,
    verifyBeforeEnable: verify,
    ...rest,
  };
}

export const ROUTE_SEED: ModelRoute[] = [
  route({
    routeId: "bedrock/claude-sonnet-5",
    providerId: "bedrock",
    modelId: "anthropic.claude-sonnet-5",
    displayName: "Claude Sonnet 5 (Bedrock)",
    contentTypes: ["text", "svg"],
    executor: "cloud",
    capabilities: { structuredJson: true },
    pricing: { unit: "token", inputPer1M: 2, outputPer1M: 10 },
    defaultParams: {},
    notes: "Normalized to the us. cross-region inference profile prefix.",
  }),
  route({
    routeId: "bedrock/claude-sonnet-4-6",
    providerId: "bedrock",
    modelId: "anthropic.claude-sonnet-4-6",
    displayName: "Claude Sonnet 4.6 (Bedrock)",
    contentTypes: ["text", "svg"],
    executor: "cloud",
    capabilities: { structuredJson: true },
    pricing: { unit: "token", inputPer1M: 3, outputPer1M: 15 },
    defaultParams: {},
    notes: "Normalized to the us. cross-region inference profile prefix. Fallback for accounts not yet enabled for Sonnet 5 on Bedrock.",
  }),
  route({
    routeId: "bedrock/claude-haiku-4-5",
    providerId: "bedrock",
    modelId: "anthropic.claude-haiku-4-5-20251001-v1:0",
    displayName: "Claude Haiku 4.5 (Bedrock)",
    contentTypes: ["text"],
    executor: "cloud",
    capabilities: { structuredJson: true },
    pricing: { unit: "token", inputPer1M: 1, outputPer1M: 5 },
    defaultParams: {},
    notes: "",
  }),
  route({
    routeId: "bedrock/nova-lite",
    providerId: "bedrock",
    modelId: "amazon.nova-lite-v1:0",
    displayName: "Amazon Nova Lite (Bedrock)",
    contentTypes: ["text"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "token", inputPer1M: 0.06, outputPer1M: 0.24 },
    defaultParams: {},
    notes: "",
    verify: true,
  }),
  route({
    routeId: "anthropic/claude-opus-5",
    providerId: "anthropic",
    modelId: "claude-opus-5",
    displayName: "Claude Opus 5 (Anthropic)",
    contentTypes: ["text", "svg"],
    executor: "cloud",
    capabilities: { structuredJson: true },
    pricing: { unit: "token", inputPer1M: 5, outputPer1M: 25 },
    defaultParams: { effort: "medium" },
    notes: "",
  }),
  route({
    routeId: "anthropic/claude-sonnet-5",
    providerId: "anthropic",
    modelId: "claude-sonnet-5",
    displayName: "Claude Sonnet 5 (Anthropic)",
    contentTypes: ["text", "svg"],
    executor: "cloud",
    capabilities: { structuredJson: true },
    pricing: { unit: "token", inputPer1M: 2, outputPer1M: 10 },
    defaultParams: { effort: "medium" },
    notes: "",
  }),
  route({
    routeId: "anthropic/claude-haiku-4-5",
    providerId: "anthropic",
    modelId: "claude-haiku-4-5",
    displayName: "Claude Haiku 4.5 (Anthropic)",
    contentTypes: ["text"],
    executor: "cloud",
    capabilities: { structuredJson: true },
    pricing: { unit: "token", inputPer1M: 1, outputPer1M: 5 },
    defaultParams: { effort: "medium" },
    notes: "",
  }),
  route({
    routeId: "openai/gpt-5",
    providerId: "openai",
    modelId: "gpt-5",
    displayName: "GPT-5 (OpenAI)",
    contentTypes: ["text", "svg"],
    executor: "cloud",
    capabilities: { structuredJson: true },
    pricing: { unit: "token" },
    defaultParams: {},
    notes: "Pricing to be filled from the vendor's models.list once a key is set.",
    verify: true,
  }),
  route({
    routeId: "openai/gpt-5-mini",
    providerId: "openai",
    modelId: "gpt-5-mini",
    displayName: "GPT-5 mini (OpenAI)",
    contentTypes: ["text"],
    executor: "cloud",
    capabilities: { structuredJson: true },
    pricing: { unit: "token" },
    defaultParams: {},
    notes: "Pricing to be filled from the vendor's models.list once a key is set.",
    verify: true,
  }),
  route({
    routeId: "openrouter/deepseek-v3-2",
    providerId: "openrouter",
    modelId: "deepseek/deepseek-v3.2",
    displayName: "DeepSeek V3.2 (OpenRouter)",
    contentTypes: ["text"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "token", inputPer1M: 0.26, outputPer1M: 0.38 },
    defaultParams: {},
    notes: "",
    verify: true,
  }),
  route({
    routeId: "openrouter/gpt-oss-120b",
    providerId: "openrouter",
    modelId: "openai/gpt-oss-120b",
    displayName: "GPT-OSS 120B (OpenRouter)",
    contentTypes: ["text"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "token", inputPer1M: 0.1, outputPer1M: 0.5 },
    defaultParams: {},
    notes: "",
    verify: true,
  }),
  route({
    routeId: "openrouter/kimi-k2",
    providerId: "openrouter",
    modelId: "moonshotai/kimi-k2",
    displayName: "Kimi K2 (OpenRouter)",
    contentTypes: ["text"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "token", inputPer1M: 0.75, outputPer1M: 3.5 },
    defaultParams: {},
    notes: "",
    verify: true,
  }),
  route({
    routeId: "openrouter/qwen3-flash",
    providerId: "openrouter",
    modelId: "qwen/qwen3.8-flash",
    displayName: "Qwen3.8 Flash (OpenRouter)",
    contentTypes: ["text"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "token", inputPer1M: 0.15, outputPer1M: 0.47 },
    defaultParams: {},
    notes: "",
    verify: true,
  }),
  route({
    routeId: "fal/flux-2-dev",
    providerId: "fal",
    modelId: "fal-ai/flux-2",
    displayName: "FLUX.2 dev (fal.ai)",
    contentTypes: ["image"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "image", perUnit: 0.012 },
    defaultParams: {},
    notes: "Priced per megapixel; perUnit is the approximate per-image cost.",
    verify: true,
  }),
  route({
    routeId: "fal/flux-2-pro",
    providerId: "fal",
    modelId: "fal-ai/flux-2-pro",
    displayName: "FLUX.2 pro (fal.ai)",
    contentTypes: ["image"],
    executor: "cloud",
    capabilities: { referenceImages: 8 },
    pricing: { unit: "image", perUnit: 0.03 },
    defaultParams: {},
    notes: "",
    verify: true,
  }),
  route({
    routeId: "fal/nano-banana",
    providerId: "fal",
    modelId: "fal-ai/nano-banana",
    displayName: "Nano Banana (fal.ai)",
    contentTypes: ["image"],
    executor: "cloud",
    capabilities: { referenceImages: 1 },
    pricing: { unit: "image", perUnit: 0.04 },
    defaultParams: { referenceModelId: "fal-ai/nano-banana/edit" },
    notes: "Reference edits go through the /edit variant named in defaultParams.referenceModelId.",
    verify: true,
  }),
  route({
    routeId: "openai/gpt-image-1-5",
    providerId: "openai",
    modelId: "gpt-image-1.5",
    displayName: "GPT Image 1.5 (OpenAI)",
    contentTypes: ["image"],
    executor: "cloud",
    capabilities: { referenceImages: 4 },
    pricing: { unit: "image", perUnit: 0.03 },
    defaultParams: {},
    notes:
      "Cost varies 0.01-0.05 by quality setting; perUnit is a mid estimate. " +
      "Verify model id and per-image price against OpenAI's live docs before enabling (Phase 5).",
    verify: true,
  }),
  route({
    routeId: "openai/gpt-image-1-mini",
    providerId: "openai",
    modelId: "gpt-image-1-mini",
    displayName: "GPT Image 1 mini (OpenAI)",
    contentTypes: ["image"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "image", perUnit: 0.005 },
    defaultParams: {},
    notes: "",
    verify: true,
  }),
  route({
    routeId: "recraft/v4-vector",
    providerId: "recraft",
    modelId: "recraftv4",
    displayName: "Recraft V4 (vector illustration)",
    contentTypes: ["svg"],
    executor: "cloud",
    capabilities: { svgNative: true },
    pricing: { unit: "image", perUnit: 0.08 },
    defaultParams: { style: "vector_illustration" },
    notes: "",
    verify: true,
  }),
  route({
    routeId: "recraft/v4-icon",
    providerId: "recraft",
    modelId: "recraftv4",
    displayName: "Recraft V4 (icon)",
    contentTypes: ["svg"],
    executor: "cloud",
    capabilities: { svgNative: true },
    pricing: { unit: "image", perUnit: 0.08 },
    defaultParams: { style: "icon" },
    notes: "",
    verify: true,
  }),
  route({
    routeId: "recraft/v4-raster",
    providerId: "recraft",
    modelId: "recraftv4",
    displayName: "Recraft V4 (raster illustration)",
    contentTypes: ["image"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "image", perUnit: 0.04 },
    defaultParams: { style: "digital_illustration" },
    notes: "",
    verify: true,
  }),
  route({
    routeId: "polly/neural",
    providerId: "polly",
    modelId: "Joanna",
    displayName: "Amazon Polly - Joanna (neural)",
    contentTypes: ["audio"],
    executor: "cloud",
    capabilities: { voices: ["Joanna"] },
    pricing: { unit: "char", perUnit: 0.000016 },
    defaultParams: { engine: "neural" },
    notes: "perUnit is USD per character ($16 / 1M chars).",
  }),
  route({
    routeId: "polly/generative",
    providerId: "polly",
    modelId: "Matthew",
    displayName: "Amazon Polly - Matthew (generative)",
    contentTypes: ["audio"],
    executor: "cloud",
    capabilities: { voices: ["Matthew"] },
    pricing: { unit: "char", perUnit: 0.00003 },
    defaultParams: { engine: "generative" },
    notes: "perUnit is USD per character ($30 / 1M chars).",
  }),
  route({
    routeId: "openai/tts",
    providerId: "openai",
    modelId: "gpt-4o-mini-tts",
    displayName: "GPT-4o mini TTS (OpenAI)",
    contentTypes: ["audio"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "char", perUnit: 0.000015 },
    defaultParams: {},
    notes: "perUnit is USD per character (~$15 / 1M chars).",
    verify: true,
  }),
  route({
    routeId: "fal/kling-2-5-turbo",
    providerId: "fal",
    modelId: "fal-ai/kling-video/v2.5-turbo/pro/text-to-video",
    displayName: "Kling 2.5 Turbo Pro (fal.ai)",
    contentTypes: ["video"],
    executor: "cloud",
    capabilities: { referenceImages: 1 },
    pricing: { unit: "second", perUnit: 0.07 },
    defaultParams: { imageToVideoModelId: "fal-ai/kling-video/v2.5-turbo/pro/image-to-video" },
    notes: "",
    verify: true,
  }),
  route({
    routeId: "fal/wan-2-5",
    providerId: "fal",
    modelId: "fal-ai/wan/v2.5/text-to-video",
    displayName: "Wan 2.5 (fal.ai)",
    contentTypes: ["video"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "second", perUnit: 0.05 },
    defaultParams: {},
    notes: "$0.05/s at 480p, up to $0.15/s at 1080p; perUnit is the 480p estimate.",
    verify: true,
  }),
  route({
    routeId: "fal/veo-3-1-lite",
    providerId: "fal",
    modelId: "fal-ai/veo3.1/lite",
    displayName: "Veo 3.1 Lite (fal.ai)",
    contentTypes: ["video"],
    executor: "cloud",
    capabilities: {},
    pricing: { unit: "second", perUnit: 0.05 },
    defaultParams: {},
    notes: "",
    verify: true,
  }),
  route({
    routeId: "local/lmstudio",
    providerId: "local-openai-compatible",
    modelId: "qwen3.6-35b-a3b",
    displayName: "LM Studio (local)",
    contentTypes: ["text", "svg"],
    executor: "local",
    capabilities: {},
    pricing: { unit: "subscription", perUnit: 0 },
    defaultParams: { baseUrl: "http://localhost:1234/v1" },
    notes: "modelId is whatever is currently loaded in LM Studio; edit to match.",
  }),
  route({
    routeId: "local/ollama",
    providerId: "local-openai-compatible",
    modelId: "gpt-oss:20b",
    displayName: "Ollama (local)",
    contentTypes: ["text", "svg"],
    executor: "local",
    capabilities: {},
    pricing: { unit: "subscription", perUnit: 0 },
    defaultParams: { baseUrl: "http://localhost:11434/v1" },
    notes: "modelId is whatever is pulled in Ollama; edit to match.",
  }),
  route({
    routeId: "local/claude-code",
    providerId: "local-claude-code",
    modelId: "sonnet",
    displayName: "claude -p (Claude Pro, local)",
    contentTypes: ["text", "svg"],
    executor: "local",
    capabilities: { structuredJson: true },
    pricing: { unit: "subscription", perUnit: 0 },
    defaultParams: {},
    notes: "Draws from the Claude Pro plan's usage limits on the owner's PC (D-23).",
  }),
  route({
    routeId: "local/codex",
    providerId: "local-codex",
    modelId: "default",
    displayName: "codex exec (ChatGPT Plus, local)",
    contentTypes: ["text", "svg"],
    executor: "local",
    capabilities: {},
    pricing: { unit: "subscription", perUnit: 0 },
    defaultParams: {},
    notes: "Draws from the ChatGPT Plus plan's usage limits on the owner's PC (D-23).",
  }),
  route({
    routeId: "local/comfyui-flux2-dev",
    providerId: "local-comfyui",
    modelId: "flux2-dev-t2i.json",
    displayName: "ComfyUI - FLUX.2 dev (local)",
    contentTypes: ["image"],
    executor: "local",
    capabilities: {},
    pricing: { unit: "subscription", perUnit: 0 },
    defaultParams: { workflow: "flux2-dev-t2i.json" },
    notes: "",
  }),
  route({
    routeId: "local/comfyui-wan22-t2v",
    providerId: "local-comfyui",
    modelId: "wan22-t2v.json",
    displayName: "ComfyUI - Wan 2.2 text-to-video (local)",
    contentTypes: ["video"],
    executor: "local",
    capabilities: {},
    pricing: { unit: "subscription", perUnit: 0 },
    defaultParams: { workflow: "wan22-t2v.json" },
    notes: "",
  }),
  route({
    routeId: "local/comfyui-wan22-i2v",
    providerId: "local-comfyui",
    modelId: "wan22-i2v.json",
    displayName: "ComfyUI - Wan 2.2 image-to-video (local)",
    contentTypes: ["video"],
    executor: "local",
    capabilities: { referenceImages: 1 },
    pricing: { unit: "subscription", perUnit: 0 },
    defaultParams: { workflow: "wan22-i2v.json" },
    notes: "",
  }),
  route({
    routeId: "local/comfyui-ltx2",
    providerId: "local-comfyui",
    modelId: "ltx2-t2v.json",
    displayName: "ComfyUI - LTX-2 (local)",
    contentTypes: ["video"],
    executor: "local",
    capabilities: {},
    pricing: { unit: "subscription", perUnit: 0 },
    defaultParams: { workflow: "ltx2-t2v.json" },
    notes: "",
  }),
  route({
    routeId: "local/kokoro",
    providerId: "local-kokoro",
    modelId: "onnx-community/Kokoro-82M-v1.0-ONNX",
    displayName: "Kokoro TTS (local)",
    contentTypes: ["audio"],
    executor: "local",
    capabilities: { voices: ["af_heart"] },
    pricing: { unit: "subscription", perUnit: 0 },
    defaultParams: { voice: "af_heart" },
    notes: "",
  }),
];

/**
 * Recommendation order per content type, used until enough per-route stats
 * exist for `recommendation.ts` to score routes on real outcomes (Task 1.1).
 */
export const DEFAULT_ORDER: Record<string, string[]> = {
  text: ["bedrock/claude-sonnet-4-6", "bedrock/claude-sonnet-5", "local/claude-code", "openrouter/deepseek-v3-2"],
  svg: ["anthropic/claude-sonnet-5", "anthropic/claude-opus-5", "recraft/v4-icon"],
  image: ["fal/flux-2-dev", "local/comfyui-flux2-dev", "openai/gpt-image-1-5"],
  audio: ["polly/neural", "local/kokoro", "openai/tts"],
  video: ["fal/wan-2-5", "local/comfyui-wan22-t2v", "fal/kling-2-5-turbo"],
};
